import React, { useMemo } from "react";

import {
  HABITAT_COLORS,
  type Habitat,
  type HistoryEntry,
  getActLabel,
} from "../vowGameLogic";
import { PANEL_CLASS } from "./uiConstants";

const HABITAT_NAMES: Record<Habitat, string> = {
  R: "Reedbed",
  P: "Meadow",
  E: "Open Water",
  B: "Woodland",
};

type StateTransitionSchemeSectionProps = {
  history: HistoryEntry[];
};

type TransitionItem = {
  key: string;
  current: HistoryEntry;
  previous?: HistoryEntry;
  habitatDelta: Record<Habitat, number> | null;
};

const StateTransitionSchemeSection: React.FC<
  StateTransitionSchemeSectionProps
> = ({ history }) => {
  const orderedHabitats: Habitat[] = useMemo(
    () => ["R", "P", "E", "B"],
    []
  );

  const entries = useMemo<TransitionItem[]>(() => {
    return history.map((entry, index) => {
      const previous = index > 0 ? history[index - 1] : undefined;
      const habitatDelta =
        previous !== undefined
          ? orderedHabitats.reduce<Record<Habitat, number>>((acc, habitat) => {
              acc[habitat] =
                entry.habitatCounts[habitat] - previous.habitatCounts[habitat];
              return acc;
            }, {} as Record<Habitat, number>)
          : null;

      return {
        key: `${entry.annee}-${index}`,
        current: entry,
        previous,
        habitatDelta,
      };
    });
  }, [history, orderedHabitats]);

  if (entries.length === 0) {
    return null;
  }

  return (
    <section className={`${PANEL_CLASS} p-6`}>
      <h2 className="text-center text-2xl font-bold text-slate-900 md:text-left">
        State & Transition Scheme
      </h2>
      <p className="mt-2 text-sm text-slate-700">
        Each act shows the simulated outcomes, the WM scenario in play, and how
        the habitat mix shifted from the previous state.
      </p>

      <div className="mt-5 space-y-4">
        {entries.map(({ key, current, previous, habitatDelta }) => (
          <article
            key={key}
            className="border border-slate-400 bg-white p-4 shadow-sm"
          >
            <header className="flex flex-wrap items-baseline justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">
                  {getActLabel(current.annee)}
                </h3>
                <p className="text-sm text-slate-700">
                  WM {current.wmChoice}
                </p>
              </div>
              <div className="text-right text-sm text-slate-600">
                <p>
                  Biodiversity:{" "}
                  <span className="font-semibold text-slate-900">
                    {current.scores.BIO}
                  </span>
                </p>
                <p>
                  Bittern Habitat:{" "}
                  <span className="font-semibold text-slate-900">
                    {current.scores.BHQ}
                  </span>
                </p>
                <p>
                  Ducks:{" "}
                  <span className="font-semibold text-slate-900">
                    {current.scores.D}
                  </span>
                </p>
              </div>
            </header>

            <div className="mt-4 grid gap-3 md:grid-cols-2 lg:grid-cols-4">
              {orderedHabitats.map((habitat) => (
                <div key={habitat} className="flex items-center gap-2 text-sm">
                  <span
                    className="h-3 w-3 rounded-sm border border-black/20"
                    style={{ backgroundColor: HABITAT_COLORS[habitat] }}
                  />
                  <span className="font-semibold text-slate-900">
                    {HABITAT_NAMES[habitat]}
                  </span>
                  <span className="text-slate-700">
                    {current.habitatCounts[habitat]} tiles
                  </span>
                </div>
              ))}
            </div>

            {previous && habitatDelta && (
              <div className="mt-4 rounded border border-slate-300 bg-slate-50 p-3 text-sm text-slate-700">
                <p className="font-semibold text-slate-900">
                  Transition from {getActLabel(previous.annee)}
                </p>
                <div className="mt-2 grid gap-2 md:grid-cols-2 lg:grid-cols-4">
                  {orderedHabitats.map((habitat) => {
                    const delta = habitatDelta[habitat];
                    if (delta === 0) {
                      return (
                        <span key={habitat}>
                          {HABITAT_NAMES[habitat]}: no change
                        </span>
                      );
                    }
                    const formatted =
                      delta > 0 ? `+${delta}` : delta.toString();
                    return (
                      <span key={habitat}>
                        {HABITAT_NAMES[habitat]}: {formatted} tiles
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {!previous && (
              <p className="mt-4 rounded border border-slate-300 bg-slate-50 p-3 text-sm text-slate-700">
                Baseline state captured at the end of the first simulation.
              </p>
            )}
          </article>
        ))}
      </div>
    </section>
  );
};

export default StateTransitionSchemeSection;
